import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { ShieldCheck, AlertCircle, Compass } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-admin min-h-[80vh] flex items-center justify-center px-5 py-10 bg-muted/30">
      <div className="w-full max-w-md bg-white border border-border rounded-2xl p-8 shadow-sm">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-[hsl(var(--primary-red))] text-white flex items-center justify-center">
            <Compass className="w-5 h-5" />
          </div>
          <span className="font-display font-semibold tracking-tight">Tour.AI</span>
        </Link>
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-5 h-5 text-[hsl(var(--primary-green))]" />
          <h1 className="text-2xl font-semibold">{t.admin.login}</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">Admin access only</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1.5">{t.admin.email}</label>
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              data-testid="admin-email-input"
              className="w-full px-4 py-2.5 border border-border rounded-md outline-none focus:ring-2 ring-[hsl(var(--primary-red))]"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1.5">{t.admin.password}</label>
            <input
              type="password"
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="w-full px-4 py-2.5 border border-border rounded-md outline-none focus:ring-2 ring-[hsl(var(--primary-red))]"
              autoComplete="current-password"
            />
          </div>
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="admin-login-error">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="w-full py-2.5 rounded-md bg-[hsl(var(--primary-red))] text-white font-semibold hover:bg-[hsl(var(--primary-red-hover))] disabled:opacity-50 transition"
          >
            {loading ? "..." : t.admin.signin}
          </button>
        </form>
      </div>
    </div>
  );
}
